import { isNil, mapValues } from "lodash";
import { isEmpty } from "underscore";
import { AssignedProductInfo, GetDelegatedEntryRightsQuery, InviteEntryRightResponse, UpdateEntryRightDelegeeModel } from "../../api/entryRights.api";
import { Statuses } from "../../components/DelegeeStatus/DelegeeStatus";
import { DelegatedEntryRight, FlatDelegee } from "../../models/delegations/DelegatedEntryRight";
import { InviteDelegeeRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { RevokeEntryRightsModel } from "../../models/vehicle-assignments/RevokeEntryRightsModel";
import { EntryRightsPagedData, EntryRightsState, MyFleetFlatDelegeeData } from "../reducers/entry-rights.reducer";
import { DelegatedParkingProductModel, DelegeeWithProductsModel } from "../../models/delegations/DelegatedParkingProductsModel";
import moment from "moment";
import { DeleteDelegeesModel } from "../../models/vehicle-assignments/DeleteDelegeesModel";
import { ParkingProductType } from "../../models";
import { ContractType, LocatedParkingProductModel, ParkingProductModel } from "../../models/ParkingProductModel";
import { UpdateEntryRightDelegeeActionThunkPayload } from "../actions/entryRights.actions";
import { DataTableContextFilters, DateFilter } from "../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FmpFilters } from "../../constants/filtering.constants";
import { ForFleetManager } from "../../models/ForFleetManager";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";
import { RevokeEntryRightsResponse } from "../../models/delegations/RevokeEntryRightsResponse";

export function reduceRevokeEntryRightsMyProducts(
    state: EntryRightsPagedData<DelegatedEntryRight>,
    revokeModel: RevokeEntryRightsModel
): EntryRightsPagedData<DelegatedEntryRight> {
    const newPages = mapValues(state.pages, (page) => {
        return {
            ...page, data: page.data?.filter((item) => !revokeModel.delegeeRevokeModels.flatMap(d => d.products)
                .map(p => p.entryRightId)
                .includes(item.id))
        };
    });

    return {
        ...state,
        pages: newPages,
        totalRecords: state.totalRecords - revokeModel.delegeeRevokeModels.flatMap(d => d.products).length,
    };
}

export function reduceDeleteEntryRightsMyProducts(
    state: EntryRightsPagedData<DelegatedEntryRight>,
    deleteModel: DeleteDelegeesModel
): EntryRightsPagedData<DelegatedEntryRight> {
    let rowsFiltered = 0;
    const newPages = mapValues(state.pages, (page) => {
        return {
            ...page, data: page.data?.filter((item) => {
                const shouldRemove = deleteModel.delegeeIds.includes(item.delegee.id);
                if (shouldRemove) {
                    rowsFiltered++;
                    return false;
                }
                return true;
            })
        };
    });

    return {
        ...state,
        pages: newPages,
        totalRecords: state.totalRecords - rowsFiltered,
    };
}

export function applyInviteEntryRightsToMyProducts(
    locatedParkingRightProducts: LocatedParkingProductModel[],
    pagedData: EntryRightsPagedData<DelegatedEntryRight>,
    request: InviteDelegeeRequestModel,
    response: InviteEntryRightResponse) {
    for (const ap of [...response.assignedProducts].reverse()) {
        const productInfo = locatedParkingRightProducts.find(p => p.placeId === ap.placeId && p.pmcId == ap.pmcId);

        if (!productInfo) {
            continue;
        }

        const pageToAddTo = Object.values(pagedData.pages)
            .find((page) => page.query.placeId === ap.placeId &&
                (!page.query.contractType || page.query.contractType === ap.contractType) &&
                (!page.query.productType || page.query.productType === ap.productType));

        if (!pageToAddTo?.data) {
            continue;
        }

        const invitationDate = new Date();
        const invitedProduct = request.products.find((product) =>
            product.pmcId === ap.pmcId &&
            product.contractType === ap.contractType &&
            product.productType === ap.productType
        );

        pageToAddTo.data.unshift({
            delegee: {
                email: request.email,
                name: request.name ?? "",
                id: response.delegeeId,
            },
            id: ap.entryRightId,
            invitationDate,
            activationDate: invitedProduct?.activationDate ?? invitationDate.toISOString(),
            expirationDate: invitedProduct?.expirationDate ?? null,
            product: {
                pmcId: ap.pmcId,
                locationName: productInfo.location,
                pmcName: productInfo.pmcName,
                placeId: ap.placeId,
                entryRightId: ap.entryRightId,
            },
            status: Statuses.Pending
        });
        pagedData.totalRecords++;
    }
}

// please call it with immer draft
export function applyEntryRightsUpdateOnDraftMyProducts(
    parkingProductsModels: ParkingProductModel[],
    pagedData: EntryRightsPagedData<DelegatedEntryRight>,
    request: UpdateEntryRightDelegeeModel,
    { response, original: originalDelegee }: UpdateEntryRightDelegeeActionThunkPayload
) {
    if (!originalDelegee) {
        throw new Error("Need original delegee to add new rows");
    }
    const createEntriesToAdd = (contractType?: ContractType, productType?: ParkingProductType) => response.assignedPedestrianProducts
        .filter((aPp) => {
            const existedAlreadyOnDelegee = originalDelegee.products.some(p => p.pmcId === aPp.pmcId && p.contractType === aPp.contractType);
            if (existedAlreadyOnDelegee) return false;

            const matchesPageQueryContractType =
                (isNil(contractType) || aPp.contractType === contractType) &&
                (isNil(productType) || aPp.productType === productType);

            return matchesPageQueryContractType;
        })
        .map((aPp): DelegatedEntryRight => {
            const product = parkingProductsModels.find((x) => x.pmcId === aPp.pmcId);
            const requestProduct = request.pmcs.find(p => p.pmcId === aPp.pmcId && p.contractType === aPp.contractType);
            return {
                delegee: {
                    email: originalDelegee.delegeeEmail,
                    name: request.name,
                    id: originalDelegee.id,
                },
                id: aPp.entryRightId,
                invitationDate: new Date(),
                product: {
                    pmcId: aPp.pmcId,
                    locationName: product?.location ?? "",
                    pmcName: product?.pmcName ?? "",
                    placeId: 42,
                    entryRightId: aPp.entryRightId,
                },
                status: originalDelegee.status,
                activationDate: requestProduct?.activationDate || undefined,
                expirationDate: requestProduct?.expirationDate || undefined,
            };
        });

    const removeCounter = originalDelegee.products.filter((x) => !request.pmcs
                .some(pmc => pmc.pmcId === x.pmcId && pmc.contractType == x.contractType)).length;
    let addedCounter = 0;

    for (const pageNumber in pagedData.pages) {
        const page = pagedData.pages[pageNumber];
        if (!page.data) {
            continue;
        }
        for (const product of page.data) {
            if (product.delegee.id === request.delegeeId) {
                product.delegee.name = request.name;
                const matchingRequestProduct = request.pmcs.find(p => p.pmcId === product.product.pmcId);
                if (matchingRequestProduct) {
                    product.activationDate = matchingRequestProduct.activationDate || undefined;
                    product.expirationDate = matchingRequestProduct.expirationDate || undefined;
                }
            }
        }
        const entriesToDelete = page.data.filter(
            (x) => x.delegee.id === request.delegeeId && !request.pmcs.some(pmc => pmc.pmcId === x.product.pmcId)
        );

        const entriesToAdd = createEntriesToAdd(page.query.contractType, page.query.productType);
        addedCounter += entriesToAdd.length;
        const matchingQueryEntries = entriesToAdd.filter((x) => pedestrianRightMatches(x, page.query));

        page.data = matchingQueryEntries.concat(page.data.filter((x) => !entriesToDelete.includes(x)));
    }
    pagedData.totalRecords = pagedData.totalRecords - removeCounter + addedCounter;
}

function findPmcInfo(state: EntryRightsState, pmcId: number): {pmcName: string, locationName: string} | undefined {
    const product = state.parkingProducts?.find((p) => p.pmcId === pmcId);
    if (product) {
        return {
            pmcName: product.pmcName,
            locationName: product.location
        };
    };

    const located = state.locatedParkingProducts.find((p) => p.pmcId === pmcId);
    if (located) {
        return {
            pmcName: located.pmcName,
            locationName: located.location
        };
    }

    return undefined;
}

function findOverviewProductInfo(
    state: EntryRightsState,
    pmcId: number
): { pmcName: string; locationName: string; placeId: number; productType: ParkingProductType } | undefined {
    const located = state.locatedParkingProducts.find((p) => p.pmcId === pmcId);
    if (located) {
        return {
            pmcName: located.pmcName,
            locationName: located.location,
            placeId: located.placeId ?? 0,
            productType: located.productType,
        };
    }

    const product = state.parkingProducts.find((p) => p.pmcId === pmcId);
    if (product) {
        return {
            pmcName: product.pmcName,
            locationName: product.location,
            placeId: product.placeId ?? 0,
            productType: product.productType,
        };
    }

    return undefined;
}

function getOverviewDataByType(
    state: EntryRightsState,
    productType: ParkingProductType
): MyFleetFlatDelegeeData {
    return productType === ParkingProductType.PedestrianAccess
        ? state.myFleetOverview.pedestrians
        : state.myFleetOverview.parkingRights;
}

function getOppositeProductType(productType: ParkingProductType): ParkingProductType {
    return productType === ParkingProductType.PedestrianAccess
        ? ParkingProductType.ParkingRights
        : ParkingProductType.PedestrianAccess;
}

function hasMyFleetProductOfType(
    state: EntryRightsState,
    delegeeId: string,
    productType: ParkingProductType
): boolean {
    const overview = getOverviewDataByType(state, productType);
    const pages = overview?.pages;
    if (!pages) {
        return false;
    }

    for (const page of Object.values(pages)) {
        if (page.data?.some((item) => item.delegee.id === delegeeId && item.product)) {
            return true;
        }
    }

    return false;
}

function getRepresentativeOverviewPage(overview: MyFleetFlatDelegeeData) {
    return Object.values(overview.pages).find((page) => page.query);
}

function normalizeOverviewRows(rows: FlatDelegee[]): FlatDelegee[] {
    const rowsByDelegee = new Map<string, FlatDelegee[]>();
    const delegeeOrder: string[] = [];

    for (const row of rows) {
        if (!rowsByDelegee.has(row.delegee.id)) {
            rowsByDelegee.set(row.delegee.id, []);
            delegeeOrder.push(row.delegee.id);
        }
        rowsByDelegee.get(row.delegee.id)!.push(row);
    }

    return delegeeOrder.flatMap((delegeeId) => {
        const groupedRows = rowsByDelegee.get(delegeeId) ?? [];
        const rowsWithProduct = groupedRows.filter((row) => row.product);
        if (rowsWithProduct.length > 0) {
            return rowsWithProduct;
        }

        const emptyRow = groupedRows[0];
        return emptyRow ? [{ ...emptyRow, product: undefined }] : [];
    });
}

function mergeOverviewRows(
    currentRows: FlatDelegee[],
    delegeeId: string,
    nextRows: FlatDelegee[]
): FlatDelegee[] {
    const existingIndices = currentRows
        .map((row, index) => row.delegee.id === delegeeId ? index : -1)
        .filter((index) => index >= 0);

    if (existingIndices.length === 0) {
        return normalizeOverviewRows(nextRows.concat(currentRows));
    }

    const firstIndex = existingIndices[0];
    const rowsWithoutDelegee = currentRows.filter((row) => row.delegee.id !== delegeeId);
    const before = rowsWithoutDelegee.slice(0, firstIndex);
    const after = rowsWithoutDelegee.slice(firstIndex);
    return normalizeOverviewRows(before.concat(nextRows, after));
}

function includesText(value: string, filterValue?: string): boolean {
    if (isNil(filterValue) || isEmpty(filterValue)) {
        return true;
    }

    return value.toLowerCase().includes(filterValue.toLowerCase());
}

function getDateFromFilterValue(value?: string | string[] | number[] | DateFilter): DateFilter | undefined {
    if (!value || Array.isArray(value) || typeof value === "string") {
        return undefined;
    }

    return value;
}

function isWithinDateFilter(dateValue: string | Date | undefined | null, filterValue?: DateFilter): boolean {
    if (!filterValue) {
        return true;
    }

    if (!dateValue) {
        return false;
    }

    const comparableDate = dateValue instanceof Date
        ? dateValue
        : moment.parseZone(dateValue).toDate();

    if (filterValue.startDate && comparableDate.getTime() < filterValue.startDate.getTime()) {
        return false;
    }

    if (filterValue.endDate && comparableDate.getTime() > filterValue.endDate.getTime()) {
        return false;
    }

    return true;
}

function matchesOverviewFilters(
    row: FlatDelegee,
    filters?: DataTableContextFilters[]
): boolean {
    return (filters ?? []).every((filter) => {
        switch (filter.key) {
            case FmpFilters.name:
                return includesText(row.delegee.name, filter.value as string | undefined);
            case FmpFilters.email:
                return includesText(row.delegee.email, filter.value as string | undefined);
            case FmpFilters.statuses: {
                const statuses = Array.isArray(filter.value) ? filter.value : [];
                return statuses.length === 0 ||
                    statuses.includes(row.status as never) ||
                    statuses.includes(String(row.status) as never);
            }
            case FmpFilters.products: {
                const pmcIds = Array.isArray(filter.value) ? filter.value.map((pmcId) => Number(pmcId)) : [];
                return pmcIds.length === 0 || (!!row.product && pmcIds.includes(row.product.pmcId));
            }
            case FmpFilters.invitationDate:
                return isWithinDateFilter(row.invitationDate, getDateFromFilterValue(filter.value));
            case FmpFilters.activationDate:
                return isWithinDateFilter(row.activationDate, getDateFromFilterValue(filter.value));
            case FmpFilters.expirationDate:
                return isWithinDateFilter(row.expirationDate, getDateFromFilterValue(filter.value));
            default:
                return true;
        }
    });
}

function filterOverviewRowsForQuery(
    rows: FlatDelegee[],
    query: WithFiltersAndPaging<ForFleetManager>
): FlatDelegee[] {
    return normalizeOverviewRows(rows.filter((row) => matchesOverviewFilters(row, query.filters)));
}

function createOverviewEmptyRow(
    baseRow: FlatDelegee,
    overrides?: Partial<FlatDelegee>
): FlatDelegee {
    return {
        ...baseRow,
        ...overrides,
        activationDate: undefined,
        expirationDate: undefined,
        product: undefined,
    };
}

function createOverviewRow(
    state: EntryRightsState,
    args: {
        delegeeId: string;
        name: string;
        email: string;
        status: Statuses;
        invitationDate: Date;
        activationDate?: string | null;
        expirationDate?: string | null;
        assignedProduct: AssignedProductInfo;
    }
): FlatDelegee | undefined {
    const productInfo = findOverviewProductInfo(state, args.assignedProduct.pmcId);
    if (!productInfo) {
        return undefined;
    }

    return {
        id: args.assignedProduct.entryRightId,
        delegee: {
            id: args.delegeeId,
            name: args.name,
            email: args.email,
        },
        invitationDate: args.invitationDate,
        activationDate: args.activationDate ?? undefined,
        expirationDate: args.expirationDate ?? undefined,
        status: args.status,
        product: {
            pmcId: args.assignedProduct.pmcId,
            pmcName: productInfo.pmcName,
            placeId: productInfo.placeId,
            locationName: productInfo.locationName,
            entryRightId: args.assignedProduct.entryRightId,
        },
    };
}

function createOverviewRowsFromAssignedProducts(
    state: EntryRightsState,
    args: {
        delegeeId: string;
        name: string;
        email: string;
        status: Statuses;
        invitationDate: Date;
        assignedProducts: AssignedProductInfo[];
        requestedProducts: { pmcId: number; contractType: ContractType; productType: ParkingProductType; activationDate?: string | null; expirationDate?: string | null; }[];
        existingRows: FlatDelegee[];
    }
): FlatDelegee[] {
    return args.assignedProducts
        .map((assignedProduct) => {
            const matchingRequestProduct = args.requestedProducts.find((requestProduct) =>
                requestProduct.pmcId === assignedProduct.pmcId &&
                requestProduct.contractType === assignedProduct.contractType &&
                requestProduct.productType === assignedProduct.productType
            );
            const existingRow = args.existingRows.find((row) => row.product?.pmcId === assignedProduct.pmcId);

            return createOverviewRow(state, {
                delegeeId: args.delegeeId,
                name: args.name,
                email: args.email,
                status: args.status,
                invitationDate: existingRow?.invitationDate ?? args.invitationDate,
                activationDate: matchingRequestProduct?.activationDate ?? existingRow?.activationDate ?? undefined,
                expirationDate: matchingRequestProduct?.expirationDate ?? existingRow?.expirationDate ?? undefined,
                assignedProduct,
            });
        })
        .filter((row): row is FlatDelegee => !!row);
}

function updateOverviewCounts(
    overview: MyFleetFlatDelegeeData,
    beforeRows: FlatDelegee[],
    afterRows: FlatDelegee[]
) {
    overview.totalRecords += afterRows.length - beforeRows.length;
    overview.totalUniqueDelegees += (afterRows.length > 0 ? 1 : 0) - (beforeRows.length > 0 ? 1 : 0);
    overview.totalRecords = Math.max(0, overview.totalRecords);
    overview.totalUniqueDelegees = Math.max(0, overview.totalUniqueDelegees);
}

function applyOverviewMutation(
    state: EntryRightsState,
    productType: ParkingProductType,
    delegeeId: string,
    createNextRows: (
        existingRows: FlatDelegee[],
        query: WithFiltersAndPaging<ForFleetManager>
    ) => FlatDelegee[]
) {
    const overview = getOverviewDataByType(state, productType);
    const representativePage = getRepresentativeOverviewPage(overview);
    const representativeBeforeRows = representativePage?.data?.filter((row) => row.delegee.id === delegeeId) ?? [];
    const representativeAfterRows = representativePage?.query
        ? createNextRows(representativeBeforeRows, representativePage.query)
        : undefined;

    for (const page of Object.values(overview.pages)) {
        const currentRows = page.data ?? [];
        const existingRows = currentRows.filter((row) => row.delegee.id === delegeeId);
        const nextRows = createNextRows(existingRows, page.query);
        page.data = mergeOverviewRows(currentRows, delegeeId, nextRows);
    }

    if (representativeAfterRows) {
        updateOverviewCounts(overview, representativeBeforeRows, representativeAfterRows);
    }
}

export function applyOptimisticUpdateOnMyFleet(
    state: EntryRightsState,
    request: UpdateEntryRightDelegeeModel,
    { response }: UpdateEntryRightDelegeeActionThunkPayload) {
    const createProduct = (x: AssignedProductInfo): DelegatedParkingProductModel | undefined => {
        const product = findPmcInfo(state, x.pmcId);
        if (!product) return undefined;
        return {
            pmcId: x.pmcId,
            parkingRightId: x.entryRightId,
            id: x.pmcId,
            name: product.pmcName,
            location: product.locationName,
            created: new Date(),
            status: Statuses.Pending,
            contractType: x.contractType,
            productType: x.productType
        };
    }

    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        const delegee = currentPage.data!.find((d) => d.registrationId === request.delegeeId);

        if (!delegee) {
            continue;
        }

        delegee.name = request.name;
        const newProducts = response.assignedPedestrianProducts
            .filter(x => !delegee.products.some(p => p.pmcId === x.pmcId))
            .map(createProduct)
            .filter((x): x is DelegatedParkingProductModel => !!x);
        const removedProducts = delegee.products.filter(p => !response.assignedPedestrianProducts.some(x => x.pmcId === p.pmcId));
        delegee.products = delegee.products.filter(p => !removedProducts.includes(p)).concat(newProducts);
    }
}

export function applyOptimisticUpdateOnMyFleetOverview(
    state: EntryRightsState,
    request: UpdateEntryRightDelegeeModel,
    { response, original }: UpdateEntryRightDelegeeActionThunkPayload
) {
    if (!original) {
        return;
    }

    for (const productType of [ParkingProductType.PedestrianAccess, ParkingProductType.ParkingRights]) {
        const assignedProducts = response.assignedPedestrianProducts.filter((product) => product.productType === productType);
        const hasOppositeTabProducts = hasMyFleetProductOfType(state, request.delegeeId, getOppositeProductType(productType));
        applyOverviewMutation(state, productType, request.delegeeId, (existingRows, query) => {
            const nextRows = createOverviewRowsFromAssignedProducts(state, {
                delegeeId: request.delegeeId,
                name: request.name,
                email: original.delegeeEmail,
                status: existingRows[0]?.status ?? original.status,
                invitationDate: existingRows[0]?.invitationDate ?? new Date(),
                assignedProducts,
                requestedProducts: request.pmcs,
                existingRows,
            });

            if (nextRows.length === 0 && existingRows.length > 0) {
                if (hasOppositeTabProducts) {
                    return [];
                }

                return filterOverviewRowsForQuery([
                    createOverviewEmptyRow(existingRows[0], {
                        delegee: {
                            ...existingRows[0].delegee,
                            name: request.name,
                            email: original.delegeeEmail,
                        },
                    }),
                ], query);
            }

            return filterOverviewRowsForQuery(nextRows, query);
        });
    }
}

export function applyOptimisticInviteOnMyFleet(state: EntryRightsState, request: InviteDelegeeRequestModel, response: InviteEntryRightResponse) {
    let wasAdded = false;
    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        const newDelegee = createNewlyInvitedDelegee(response, request, state);
        if(newDelegee.products.find(p => p.productType == ParkingProductType.PedestrianAccess) === undefined) {
            return;
        }
        currentPage.data!.unshift(newDelegee);
        wasAdded = true;
    }

    if (wasAdded) {
        state.myFleet.delegees.totalRecords += 1;
    }
}

export function applyOptimisticInviteOnMyFleetOverview(
    state: EntryRightsState,
    request: InviteDelegeeRequestModel,
    response: InviteEntryRightResponse
) {
    const invitationDate = new Date();

    for (const productType of [ParkingProductType.PedestrianAccess, ParkingProductType.ParkingRights]) {
        const assignedProducts = response.assignedProducts.filter((product) => product.productType === productType);
        if (assignedProducts.length === 0) {
            continue;
        }

        applyOverviewMutation(state, productType, response.delegeeId, (_, query) => {
            const nextRows = createOverviewRowsFromAssignedProducts(state, {
                delegeeId: response.delegeeId,
                name: request.name ?? "",
                email: request.email,
                status: Statuses.Pending,
                invitationDate,
                assignedProducts,
                requestedProducts: request.products,
                existingRows: [],
            });

            return filterOverviewRowsForQuery(nextRows, query);
        });
    }
}

function createNewlyInvitedDelegee(response: InviteEntryRightResponse, request: InviteDelegeeRequestModel, state: EntryRightsState): DelegeeWithProductsModel {
    return {
        registrationId: response.delegeeId,
        name: request.name ?? "",
        emailAddress: request.email,
        invitationAccepted: false,
        invitationDate: moment().format("DD/MM/YYYY"),
        products: response.assignedProducts.map((x): DelegatedParkingProductModel => {
            const product = state.parkingProducts.find((p) => p.pmcId === x.pmcId);
            return {
                pmcId: x.pmcId,
                parkingRightId: x.entryRightId,
                id: x.pmcId,
                name: product?.pmcName || "",
                location: product?.location || "",
                created: new Date(),
                status: Statuses.Pending,
                contractType: x.contractType,
                productType: x.productType
            };
        }),
    };
}

export function applyOptimisticDeleteMyFleet(state: EntryRightsState, deleteModel: DeleteDelegeesModel) {
    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        currentPage.data = currentPage.data?.filter((d) => !deleteModel.delegeeIds.includes(d.registrationId));
    }
    state.myFleet.delegees.totalRecords -= deleteModel.delegeeIds.length;
}

export function applyOptimisticDeleteMyFleetOverview(state: EntryRightsState, deleteModel: DeleteDelegeesModel) {
    for (const delegeeId of deleteModel.delegeeIds) {
        for (const productType of [ParkingProductType.PedestrianAccess, ParkingProductType.ParkingRights]) {
            applyOverviewMutation(state, productType, delegeeId, () => []);
        }
    }
}

export function applyOptimisticRevokeOnMyFleet(state: EntryRightsState, revokeModel: RevokeEntryRightsModel) {
    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        for (const delegeeRevoke of revokeModel.delegeeRevokeModels) {
            const delegee = currentPage.data?.find(d => d.registrationId === delegeeRevoke.delegeeId);
            if (delegee) {
                delegee.products = delegee.products.filter(
                    p => !delegeeRevoke.products.map(p => p.entryRightId).includes(p.parkingRightId)
                );
            }
        }
    }
}

export function applyRevokeResponseOnMyFleetOverview(
    state: EntryRightsState,
    revokeModel: RevokeEntryRightsModel,
    response: RevokeEntryRightsResponse
) {
    const responseDelegees = new Map(
        (response.result?.delegees ?? []).map((delegee) => [delegee.delegeeId, delegee])
    );

    for (const delegeeRevoke of revokeModel.delegeeRevokeModels) {
        const responseDelegee = responseDelegees.get(delegeeRevoke.delegeeId);

        for (const productType of [ParkingProductType.PedestrianAccess, ParkingProductType.ParkingRights]) {
            const hasOppositeTabProducts = hasMyFleetProductOfType(state, delegeeRevoke.delegeeId, getOppositeProductType(productType));

            applyOverviewMutation(state, productType, delegeeRevoke.delegeeId, (existingRows, query) => {
                if (!responseDelegee) {
                    const revokedEntryRightIds = new Set(delegeeRevoke.products.map((product) => product.entryRightId));
                    const remainingRows = existingRows.filter((row) => !row.product || !revokedEntryRightIds.has(row.product.entryRightId));
                    const normalizedRows = normalizeOverviewRows(remainingRows);
                    const shouldCreateEmptyRow = existingRows.some((row) => row.product) && !normalizedRows.some((row) => row.product);

                    if (shouldCreateEmptyRow && hasOppositeTabProducts) {
                        return [];
                    }

                    if (shouldCreateEmptyRow) {
                        return filterOverviewRowsForQuery([
                            createOverviewEmptyRow(existingRows[0]),
                        ], query);
                    }

                    return filterOverviewRowsForQuery(normalizedRows, query);
                }

                const assignedProducts = responseDelegee.productInfo.filter((product) => product.productType === productType);

                if (assignedProducts.length === 0) {
                    if (hasOppositeTabProducts) {
                        return [];
                    }

                    if (existingRows.length === 0) {
                        return [];
                    }

                    return filterOverviewRowsForQuery([
                        createOverviewEmptyRow(existingRows[0], {
                            delegee: {
                                ...existingRows[0].delegee,
                                name: responseDelegee.name,
                                email: responseDelegee.email,
                            },
                        }),
                    ], query);
                }

                const invitationDate = responseDelegee.creationDate ? new Date(responseDelegee.creationDate) : new Date();
                const nextRows = assignedProducts
                    .map((productInfo) => {
                        const existingRow = existingRows.find((row) => row.product?.entryRightId === productInfo.entryRightId);

                        return createOverviewRow(state, {
                            delegeeId: responseDelegee.delegeeId,
                            name: responseDelegee.name,
                            email: responseDelegee.email,
                            status: productInfo.delegeeStatus ?? existingRows[0]?.status ?? Statuses.Pending,
                            invitationDate: existingRow?.invitationDate ?? invitationDate,
                            activationDate: productInfo.activationDate,
                            expirationDate: productInfo.expirationDate,
                            assignedProduct: {
                                pmcId: productInfo.pmcId,
                                entryRightId: productInfo.entryRightId,
                                contractId: productInfo.contractId,
                                contractType: productInfo.contractType,
                                productType: productInfo.productType,
                                placeId: productInfo.placeId,
                            },
                        });
                    })
                    .filter((row): row is FlatDelegee => !!row);

                return filterOverviewRowsForQuery(nextRows, query);
            });
        }
    }
}

function pedestrianRightMatches(x: DelegatedEntryRight, query: GetDelegatedEntryRightsQuery): boolean {
    return (
        (isNil(query.pmcIds) || isEmpty(query.pmcIds) || query.pmcIds.includes(x.product.pmcId)) &&
        (isNil(query.email) || isEmpty(query.email) || x.delegee.email.includes(query.email)) &&
        (isNil(query.name) || isEmpty(query.name) || x.delegee.name.includes(query.name)) &&
        (isNil(query.statuses) || isEmpty(query.statuses) || query.statuses.includes(x.status)) &&
        (isNil(query.invitationFrom) || x.invitationDate.getTime() >= new Date(query.invitationFrom).getTime()) &&
        (isNil(query.invitationTo) || x.invitationDate.getTime() <= new Date(query.invitationTo).getTime())
    );
}
